import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import ImageUploader from '../../components/app/ImageUploader';
import { EVENT_CATEGORIES, EVENT_EMOJI } from '../../constants/events';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/api';
import AIDescriptionHelper from '../../components/app/AIDescriptionHelper';
import LocationInput from '../../components/app/LocationInput';
import { slugify } from '../../utils/slug';

type Visibility = 'community' | 'global' | 'private';

const inputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: '#f7f8fc', border: '1.5px solid #e2e6f0', borderRadius: '10px',
  padding: '0.875rem', color: '#1a1f36', fontSize: '0.9rem', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s',
};
const labelStyle: React.CSSProperties = {
  display: 'block', color: '#4a5568', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: 600,
};
const sectionStyle: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #eef0f6', borderRadius: '16px',
  padding: '1.5rem', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};

function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowPlusTwoHours() {
  const d = new Date(Date.now() + 2 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AppCreateEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const editCommunity = searchParams.get('community');
  const isEditMode = !!editId;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [eventDate, setEventDate] = useState(nowPlusTwoHours());
  const [endTime, setEndTime] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<Visibility>('community');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCommunity, setUserCommunity] = useState('');

  useEffect(() => {
    apiGet<{ data: { community?: string; nationality?: string; city?: string } }>('/api_profile', undefined, true)
      .then(res => {
        const d = res.data;
        setUserCommunity(d.community || (d.nationality && d.city ? `${d.nationality} in ${d.city}` : ''));
      })
      .catch(() => {});
  }, []);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);

  useEffect(() => {
    if (!isEditMode) return;
    const params: Record<string, string> = { id: editId! };
    if (editCommunity) params.community = editCommunity;
    apiGet<{ data: any }>('/api-event-details', params, true)
      .then((res) => {
        const ev = res.data;
        setTitle(ev.title || '');
        setDescription(ev.description || '');
        setCategory(ev.category || '');
        setLocation(ev.location || '');
        setImages(ev.images || []);
        setTags(ev.tags || []);
        setTicketUrl(ev.ticket_url || '');
        if (ev.event_date) setEventDate(toDatetimeLocal(ev.event_date));
        if (ev.end_time) setEndTime(toDatetimeLocal(ev.end_time));
        setVisibility((ev.type as Visibility) || 'community');
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load event'))
      .finally(() => setLoadingEdit(false));
  }, [isEditMode, editId, editCommunity]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 10) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Event title is required.'); return; }
    if (!description.trim()) { setError('Description is required.'); return; }
    if (!category) { setError('Please select a category.'); return; }
    if (!location.trim()) { setError('Location is required.'); return; }
    if (!eventDate) { setError('Event date is required.'); return; }

    setSubmitting(true);
    setError(null);

    const payload: Record<string, unknown> = {
      title: title.trim(),
      description: description.trim(),
      category,
      location: location.trim(),
      ...(locationCoords ? { latitude: locationCoords.lat, longitude: locationCoords.lng } : {}),
      event_date: new Date(eventDate).toISOString(),
      type: visibility,
      images,
    };
    if (endTime) payload.end_time = new Date(endTime).toISOString();
    if (ticketUrl.trim()) payload.ticket_url = ticketUrl.trim();
    if (tags.length > 0) payload.tags = tags;

    try {
      if (isEditMode) {
        const params: Record<string, string> = { id: editId! };
        if (editCommunity) params.community = editCommunity;
        await apiPut('/api-update-event', payload, params);
        navigate(`/events/${slugify(title)}-${editId}?community=${encodeURIComponent(editCommunity || '')}`);
      } else {
        const res = await apiPost<{ data?: { id: string }; id?: string }>('/api-save-event', payload, true);
        const newId = res.data?.id || res.id;
        navigate(newId ? `/events/${slugify(title)}-${newId}` : '/events');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} event.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const params: Record<string, string> = { id: editId! };
      if (editCommunity) params.community = editCommunity;
      await apiDelete('/api-delete-event', params);
      navigate('/events');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete event.');
    } finally {
      setDeleting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
        <AppNav />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
      <AppNav />

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '1.75rem 1.25rem 4rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <button onClick={() => navigate(isEditMode ? `/events/${editId}` : '/events')}
            style={{ background: 'none', border: 'none', color: '#6b7a99', fontSize: '0.875rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 500 }}>
            ← {isEditMode ? 'Back to Event' : 'All Events'}
          </button>
          {isEditMode && (
            <button onClick={handleDelete} disabled={deleting}
              style={{ background: 'none', border: '1.5px solid #fca5a5', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', cursor: 'pointer', padding: '0.35rem 0.75rem', fontWeight: 600, opacity: deleting ? 0.6 : 1 }}>
              {deleting ? 'Deleting...' : 'Delete Event'}
            </button>
          )}
        </div>

        <h1 style={{ color: '#1a1f36', fontWeight: 800, fontSize: '1.625rem', margin: '0 0 1.75rem', letterSpacing: '-0.03em' }}>
          {isEditMode ? 'Edit Event' : 'Create an Event'}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Basic Info</h2>
            <div style={{ marginBottom: '1.125rem' }}>
              <label style={labelStyle}>Event Title <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Diwali Celebration 2025" maxLength={200} style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <label style={{ ...labelStyle, margin: 0 }}>Description <span style={{ color: '#ef4444' }}>*</span></label>
                <AIDescriptionHelper itemType="event" currentText={[title, description].filter(Boolean).join('\n')} onAccept={setDescription} />
              </div>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell people about this event — what to expect, who it's for, what to bring..." rows={4} maxLength={2000}
                style={{ ...inputStyle, resize: 'vertical' }} onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
              <p style={{ color: '#8896ab', fontSize: '0.75rem', margin: '0.25rem 0 0', textAlign: 'right' }}>{description.length}/2000</p>
            </div>
          </div>

          {/* Category */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Category <span style={{ color: '#ef4444' }}>*</span></h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
              {EVENT_CATEGORIES.map((cat) => {
                const active = category === cat;
                return (
                  <button key={cat} type="button" onClick={() => setCategory(cat)}
                    style={{ padding: '0.625rem 0.5rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontSize: '0.82rem', fontWeight: active ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', transition: 'all 0.15s' }}>
                    <span>{EVENT_EMOJI[cat]}</span><span>{cat}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date & Time */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Date & Time</h2>
            <div style={{ marginBottom: '1.125rem' }}>
              <label style={labelStyle}>Start Date & Time <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
            </div>
            <div>
              <label style={labelStyle}>End Date & Time <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional)</span></label>
              <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
            </div>
          </div>

          {/* Location */}
          <div style={sectionStyle}>
            <LocationInput value={location} onChange={setLocation} onCoordinates={(lat, lng) => setLocationCoords({ lat, lng })} placeholder="e.g. Hyde Park, London or Online (Zoom)" required inputStyle={inputStyle} />
          </div>

          {/* Visibility */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Visibility</h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {([
                { val: 'community', label: '🏘️ Community' },
                { val: 'global',    label: `🌍 Global in ${userCommunity?.split(' in ')[1] || 'your city'}` },
                { val: 'private',   label: '🔒 Private' },
              ] as { val: Visibility; label: string }[]).map(({ val, label }) => {
                const active = visibility === val;
                return (
                  <button key={val} type="button" onClick={() => setVisibility(val)}
                    style={{ flex: '1 1 120px', padding: '0.75rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.15s' }}>
                    {label}
                  </button>
                );
              })}
            </div>
            <p style={{ color: '#8896ab', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>
              {visibility === 'community'
                ? `Community — Visible to "${userCommunity || 'your community'}"`
                : visibility === 'global'
                  ? `Global in ${userCommunity?.split(' in ')[1] || 'your city'} — Visible to everyone in your city`
                  : 'Private — Members must request to join and be approved.'}
            </p>
          </div>

          {/* Ticket URL */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.375rem' }}>
              Ticket / Registration Link <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional)</span>
            </h2>
            <p style={{ color: '#8896ab', fontSize: '0.78rem', margin: '0 0 0.875rem' }}>Link for attendees to register or buy tickets.</p>
            <input type="url" value={ticketUrl} onChange={(e) => setTicketUrl(e.target.value)} placeholder="https://eventbrite.com/... or https://forms.google.com/..." style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
          </div>

          {/* Tags */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>
              Tags <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional)</span>
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="e.g. family-friendly" style={{ ...inputStyle, flex: 1 }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
              <button type="button" onClick={addTag}
                style={{ padding: '0 1.25rem', backgroundColor: '#FF385C', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 }}>
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {tags.map((t) => (
                  <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.7rem', backgroundColor: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: '20px', fontSize: '0.8rem', color: '#3730a3', fontWeight: 500 }}>
                    {t}
                    <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.9rem', padding: 0, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Images <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional, up to 3)</span></h2>
            <ImageUploader value={images} onChange={setImages} maxImages={3} />
          </div>

          {error && (
            <div style={{ padding: '0.875rem 1rem', backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '10px', color: '#c53030', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ width: '100%', padding: '1rem', backgroundColor: '#FF385C', border: 'none', borderRadius: '12px', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.75 : 1, transition: 'all 0.15s', boxShadow: '0 4px 16px rgba(255,56,92,0.3)' }}>
            {submitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes ✨' : 'Create Event ✨')}
          </button>
        </form>
      </div>
    </div>
  );
}
